import { mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Tooltip } from '@mui/material';
import React, { useCallback, useContext } from 'react';
import { CellProps } from 'react-table';
import type { RosterRecord } from '../../services/roster/roster.types';
import { ToastContext } from '../../services/toast/toast';
import { RosterDisableError, RosterStore } from '../../stores/roster/roster.store';
import { BaseButton } from '../base/button/button';
import { TooltipTransitionComponent } from '../base/tooltip/tooltip-transition';
import type { ITableColumn } from '../data-table/data-table.presenter';
import { DisableForm } from './roster-bulk-dialogs';
import { useRosterDialog } from './roster-dialog';
import * as S from './roster-table.styles';

export function rosterNoun(tab: RosterStore['tab'], count: number): string {
    if (tab === 'providers') {
        return count === 1 ? 'provider' : 'providers';
    }
    return count === 1 ? 'facility' : 'facilities';
}

/**
 * Confirm-and-disable for the bulk bar — it warns that disabling
 * blocks claim submission for claims that reference the record (see `DisableForm`).
 *
 * `onDone` gets the ids that should stay selected: none after a success, and after a failure only
 * those still on the roster — a disable can fail partway, and the ones it did disable are gone.
 */
export function useRosterDisable(
    store: RosterStore,
): (ids: string[], onDone?: (stillSelected: string[]) => void) => void {
    const dialog = useRosterDialog();
    const toast = useContext(ToastContext);

    return useCallback(
        (ids: string[], onDone?: (stillSelected: string[]) => void): void => {
            const noun = rosterNoun(store.tab, ids.length);
            const close = (): void => dialog.hide();
            dialog.show({
                props: { onClose: close, renderTitle: `Disable ${ids.length} ${noun}?` },
                Component: (
                    <DisableForm
                        count={ids.length}
                        noun={noun}
                        onCancel={close}
                        onSubmit={async (): Promise<void> => {
                            try {
                                await store.disable(ids);
                                onDone?.([]);
                                close();
                                toast.notify?.(`${ids.length} ${noun} disabled.`, 'right', 'light');
                            } catch (error) {
                                onDone?.(error instanceof RosterDisableError ? error.stillActive : ids);
                                close();
                                toast.notify?.(
                                    `Disabling failed: ${error instanceof Error ? error.message : 'request failed'}.`,
                                    'right',
                                    'warning',
                                );
                            }
                        }}
                    />
                ),
            });
        },
        [dialog, toast, store],
    );
}

/**
 * The row's trailing column: the design's chevron, "Open details", which opens the record in the
 * same drawer "Add provider" uses. Built from the same core pieces the support tables' actions
 * column renders a starred action with — a borderless fab `BaseButton` under a tooltip — so it
 * looks the same in both apps. `Actions` / an empty header keep it out of the column picker, as
 * that column was. The click stops at the cell: the row's own click opens the record too.
 */
export function rosterOpenColumn(open: (id: string) => void): ITableColumn<RosterRecord> {
    return {
        id: 'Actions',
        Header: '',
        accessor: (): string => '',
        maxWidth: 100,
        minWidth: 100,
        disableSortBy: true,
        Cell: ({ row }: CellProps<RosterRecord>): React.ReactElement => (
            <S.ActionCellWrapper onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
                <Tooltip placement="top" title="Open details" TransitionComponent={TooltipTransitionComponent}>
                    <div>
                        <BaseButton
                            variant="fab-light"
                            noBorder={true}
                            aria-label="Open details"
                            onClick={(): void => open(row.original.id)}
                        >
                            <Icon path={mdiChevronRight} size={0.9} />
                        </BaseButton>
                    </div>
                </Tooltip>
            </S.ActionCellWrapper>
        ),
    } as ITableColumn<RosterRecord>;
}
